import React, { useEffect, useState } from 'react';
import { useCommands } from '../api/commands';
import { useOrg } from '../org/OrgContext';
import PageHeader from '../components/PageHeader';

/**
 * Teams management page
 * Works for GFM-first, with fallback to generic /gpm teams where applicable.
 */
export default function Teams(){
  const cmd = useCommands();
  const { org } = useOrg();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [newTeam, setNewTeam] = useState('');

  // Inline rename: `editing` is the current name of the team being renamed
  const [editing, setEditing] = useState(null);
  const [renameTo, setRenameTo] = useState('');
  const [saving, setSaving] = useState(false);

  // -----------------------------
  // Helpers: pick correct API (GFM preferred)
  // -----------------------------
  const api = cmd.gfmTeams ?? cmd.teams;

  async function load(){
    setLoading(true); setError('');
    try{
      if (!api?.list) throw new Error('Teams API not available');
      const list = await api.list();
      const names = (Array.isArray(list) ? list : [])
        .map(x => typeof x === 'string' ? x : (x?.name || x?.id || ''))
        .filter(Boolean);
      setRows(Array.from(new Set(names)).sort((a,b)=>a.localeCompare(b)));
    }catch(e){
      setError(e?.response?.data?.error || e?.message || 'Failed to load teams');
    }finally{ setLoading(false); }
  }
  useEffect(()=>{ load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function addTeam(e){
    e.preventDefault();
    setNotice('');
    const name = newTeam.trim();
    if (!name) return;
    if (rows.some(r => r.toLowerCase() === name.toLowerCase())){
      setError(`Team "${name}" already exists`);
      return;
    }
    try{
      if (!api?.add) throw new Error('Add team not supported');
      await api.add(name);
      setNewTeam('');
      await load();
    }catch(e){
      setError(e?.response?.data?.error || e?.message || 'Failed to add team');
    }
  }

  function startRename(name){
    setEditing(name);
    setRenameTo(name);
    setError('');
    setNotice('');
  }

  function cancelRename(){
    setEditing(null);
    setRenameTo('');
  }

  async function saveRename(e){
    e.preventDefault();
    const from = editing;
    const to = renameTo.trim();
    if (!from || !to) return;
    if (to === from) { cancelRename(); return; }
    if (rows.some(r => r !== from && r.toLowerCase() === to.toLowerCase())){
      setError(`Team "${to}" already exists`);
      return;
    }
    setSaving(true); setError('');
    try{
      if (!api?.rename) throw new Error('Rename team not supported');
      const res = await api.rename(from, to);
      const updated = Number(res?.historyEntriesUpdated || 0);
      cancelRename();
      setNotice(
        `Renamed "${from}" to "${to}".` +
        (updated ? ` ${updated} history ${updated === 1 ? 'entry' : 'entries'} updated.` : '')
      );
      await load();
    }catch(e){
      setError(e?.response?.data?.error || e?.message || 'Failed to rename team');
    }finally{
      setSaving(false);
    }
  }

  async function deleteTeam(name){
    if (!window.confirm(`Delete team "${name}"? This cannot be undone.`)) return;
    setNotice('');
    try{
      if (!api?.delete) throw new Error('Delete team not supported');
      await api.delete(name);
      await load();
    }catch(e){
      setError(e?.response?.data?.error || e?.message || 'Failed to delete team');
    }
  }

  return (
    <div className="container narrow">
      <PageHeader
        eyebrow={org}
        title="Teams"
        actions={<button className="btn secondary" onClick={load} disabled={loading}>Reload</button>}
      />

      {(error || notice) && (
        <div className="page-alerts">
          {error && <div className="alert error">{error}</div>}
          {notice && <div className="alert success">{notice}</div>}
        </div>
      )}

      <div className="card mb-16">
        <div className="card-head">
          <h2 className="card-title">Add a team</h2>
        </div>
        <form className="inline-form" onSubmit={addTeam}>
          <input
            className="input"
            placeholder="Team name"
            aria-label="New team name"
            value={newTeam}
            onChange={e=>setNewTeam(e.target.value)}
          />
          <button className="btn" type="submit" disabled={loading || !newTeam.trim()}>Add team</button>
        </form>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">All teams</h2>
          {!loading && <span className="badge">{rows.length}</span>}
        </div>

        {loading ? <div className="empty">Loading</div> : rows.length === 0 ? (
          <div className="empty">No teams yet.</div>
        ) : (
          <ul className="list">
            {rows.map(n => (
              <li key={n} className={`list-row${editing === n ? ' editing' : ''}`}>
                {editing === n ? (
                  <form className="rename-form" onSubmit={saveRename}>
                    <input
                      className="input"
                      value={renameTo}
                      onChange={e=>setRenameTo(e.target.value)}
                      onKeyDown={e=>{ if (e.key === 'Escape') cancelRename(); }}
                      aria-label={`New name for ${n}`}
                      autoFocus
                    />
                    <button className="btn small" type="submit" disabled={saving || !renameTo.trim()}>
                      {saving ? 'Saving' : 'Save'}
                    </button>
                    <button className="btn secondary small" type="button" onClick={cancelRename} disabled={saving}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <span className="list-row-name">{n}</span>
                    <div className="list-row-actions">
                      <button className="btn secondary small" onClick={()=>startRename(n)} disabled={saving}>
                        Rename
                      </button>
                      <button className="btn secondary small destructive" onClick={()=>deleteTeam(n)} disabled={saving}>
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
